import { Button, Chip, Input } from '@emailforge/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type DetectResult,
  type DnsInstruction,
  type DnsVerifyResult,
  type EspId,
  useDetectSmtpMutation,
  useSmtpDnsInstructionsMutation,
  useVerifySmtpDnsMutation,
} from '@/features/smtp-onboard/smtpOnboardApi';

type Step = 'domain' | 'detect' | 'instructions' | 'verify';

/**
 * Three-step SMTP onboarding wizard:
 *   1. User types their domain.
 *   2. We resolve MX + SPF and infer their current/preferred ESP.
 *   3. We show the DNS records they must publish.
 *   4. Live verify: SPF + DKIM + DMARC.
 *
 * Each step is independent — the user can re-run any step without restarting.
 */
export function SmtpOnboardWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('domain');
  const [domain, setDomain] = useState('');
  const [providerType, setProviderType] = useState<EspId>('resend');
  const [detectResult, setDetectResult] = useState<DetectResult | null>(null);
  const [instructions, setInstructions] = useState<DnsInstruction[] | null>(null);
  const [verifyResult, setVerifyResult] = useState<DnsVerifyResult | null>(null);

  const [detectMut, detectState] = useDetectSmtpMutation();
  const [instructionsMut, instructionsState] = useSmtpDnsInstructionsMutation();
  const [verifyMut, verifyState] = useVerifySmtpDnsMutation();

  const runDetect = async () => {
    const r = await detectMut({ domain }).unwrap();
    setDetectResult(r);
    if (r.inferredEsp !== 'unknown' && r.inferredEspSupported) {
      setProviderType(r.inferredEsp);
    }
    setStep('detect');
  };

  const runInstructions = async (esp: EspId) => {
    setProviderType(esp);
    const r = await instructionsMut({ domain, providerType: esp }).unwrap();
    setInstructions(r);
    setStep('instructions');
  };

  const runVerify = async () => {
    const r = await verifyMut({ domain, providerType }).unwrap();
    setVerifyResult(r);
    setStep('verify');
  };

  return (
    <>
      <TopBar
        crumbs={['SMTP', 'Onboard']}
        right={
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/smtp')}>
            ← Back to SMTP
          </Button>
        }
      />
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px' }}>
          <h1 className="display" style={{ fontSize: 36, margin: '0 0 8px', letterSpacing: -0.02 }}>
            Onboard your sending <i style={{ color: 'var(--flame)' }}>domain</i>
          </h1>
          <p style={{ fontSize: 14, color: 'var(--ink-2)', maxWidth: 600, lineHeight: 1.55 }}>
            We'll detect your current ESP from your DNS records, hand you a copy-paste DNS block,
            and verify SPF + DKIM + DMARC live. No credentials needed for detection.
          </p>

          <Stepper step={step} />

          <DomainStep
            domain={domain}
            onChange={setDomain}
            onRun={runDetect}
            isRunning={detectState.isLoading}
            error={detectState.error ? 'Lookup failed — try again.' : null}
          />

          {detectResult && (
            <DetectStep
              detect={detectResult}
              currentEsp={providerType}
              onChooseEsp={(esp) => runInstructions(esp)}
              isLoading={instructionsState.isLoading}
            />
          )}

          {instructions && (
            <InstructionsStep
              instructions={instructions}
              onVerify={runVerify}
              isVerifying={verifyState.isLoading}
            />
          )}

          {verifyResult && <VerifyStep result={verifyResult} onRecheck={runVerify} />}
        </div>
      </div>
    </>
  );
}

function Stepper({ step }: { step: Step }) {
  const order: Step[] = ['domain', 'detect', 'instructions', 'verify'];
  const labels: Record<Step, string> = {
    domain: 'Domain',
    detect: 'Detect ESP',
    instructions: 'Publish DNS',
    verify: 'Verify',
  };
  const idx = order.indexOf(step);
  return (
    <div
      style={{
        display: 'flex',
        gap: 0,
        margin: '24px 0',
        padding: '12px 16px',
        background: 'var(--paper)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-2)',
      }}
    >
      {order.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              className="mono"
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: done ? 'var(--spark)' : active ? 'var(--flame)' : 'var(--paper-3)',
                color: done || active ? 'white' : 'var(--ink-3)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {done ? <Icon.check size={11} /> : i + 1}
            </span>
            <span
              style={{
                fontSize: 12.5,
                fontWeight: active ? 600 : 500,
                color: active ? 'var(--ink)' : 'var(--ink-2)',
              }}
            >
              {labels[s]}
            </span>
            {i < order.length - 1 && (
              <div style={{ flex: 1, height: 1, background: 'var(--line-soft)', margin: '0 8px' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function DomainStep({
  domain,
  onChange,
  onRun,
  isRunning,
  error,
}: {
  domain: string;
  onChange: (v: string) => void;
  onRun: () => void;
  isRunning: boolean;
  error: string | null;
}) {
  return (
    <Card title="1 · Your domain">
      <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12, lineHeight: 1.5 }}>
        The bare domain you'll send <em>from</em> — e.g. <code className="mono">acme.com</code> if your
        from-address is <code className="mono">team@acme.com</code>.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <Input
          value={domain}
          onChange={(e) => onChange(e.target.value)}
          placeholder="acme.com"
          style={{ flex: 1 }}
          autoFocus
        />
        <Button variant="flame" onClick={onRun} disabled={!domain || isRunning}>
          {isRunning ? 'Looking up…' : 'Detect ESP'}
        </Button>
      </div>
      {error && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--err)' }}>{error}</div>
      )}
    </Card>
  );
}

function DetectStep({
  detect,
  currentEsp,
  onChooseEsp,
  isLoading,
}: {
  detect: DetectResult;
  currentEsp: EspId;
  onChooseEsp: (esp: EspId) => void;
  isLoading: boolean;
}) {
  const ESPS: Array<{ id: EspId; name: string }> = [
    { id: 'resend', name: 'Resend' },
    { id: 'postmark', name: 'Postmark' },
    { id: 'mailgun', name: 'Mailgun' },
    { id: 'sendgrid', name: 'SendGrid' },
    { id: 'amazonses', name: 'Amazon SES' },
  ];
  return (
    <Card title="2 · Detected ESP">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        {detect.inferredEsp !== 'unknown' ? (
          detect.inferredEspSupported ? (
            <Chip variant="live">Detected: {detect.inferredEspName}</Chip>
          ) : (
            <Chip variant="flame">Detected: {detect.inferredEspName} (not yet supported)</Chip>
          )
        ) : (
          <Chip>No ESP detected — pick one to onboard with</Chip>
        )}
      </div>
      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 10 }}>
        MX: {detect.mx.length > 0 ? detect.mx.join(', ') : '(none)'} · SPF:{' '}
        {detect.spf ? detect.spf.slice(0, 60) + (detect.spf.length > 60 ? '…' : '') : '(none)'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
        {ESPS.map((e) => (
          <button
            key={e.id}
            onClick={() => onChooseEsp(e.id)}
            disabled={isLoading}
            style={{
              padding: 10,
              background: currentEsp === e.id ? 'var(--flame-soft)' : 'var(--paper)',
              border: `1px solid ${currentEsp === e.id ? 'var(--flame)' : 'var(--line)'}`,
              borderRadius: 'var(--r-2)',
              cursor: 'pointer',
              fontSize: 12.5,
              fontWeight: currentEsp === e.id ? 600 : 500,
              color: currentEsp === e.id ? 'var(--flame-2)' : 'var(--ink)',
            }}
          >
            {e.name}
          </button>
        ))}
      </div>
    </Card>
  );
}

function InstructionsStep({
  instructions,
  onVerify,
  isVerifying,
}: {
  instructions: DnsInstruction[];
  onVerify: () => void;
  isVerifying: boolean;
}) {
  return (
    <Card title="3 · DNS records to publish">
      <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12, lineHeight: 1.5 }}>
        Add these at your DNS host (Cloudflare, Route 53, GoDaddy, etc.). Replace the placeholder
        DKIM value with the actual key from your ESP dashboard.
      </p>
      <table className="mf-table" style={{ width: '100%', fontSize: 12.5, marginBottom: 14 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Category</th>
            <th>Type</th>
            <th style={{ textAlign: 'left' }}>Host</th>
            <th style={{ textAlign: 'left' }}>Value</th>
          </tr>
        </thead>
        <tbody>
          {instructions.map((i, idx) => (
            <tr key={idx}>
              <td>{i.category}</td>
              <td className="mono">{i.type}</td>
              <td className="mono" style={{ fontSize: 11 }}>
                {i.host}
              </td>
              <td className="mono" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                {i.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="flame" onClick={onVerify} disabled={isVerifying}>
        {isVerifying ? 'Checking DNS…' : 'Verify DNS'}
      </Button>
    </Card>
  );
}

function VerifyStep({
  result,
  onRecheck,
}: {
  result: DnsVerifyResult;
  onRecheck: () => void;
}) {
  const allOk = result.spf.status === 'verified' && result.dkim.status === 'verified' && result.dmarc.status === 'verified';
  return (
    <Card title="4 · Verification">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <VerifyRow label="SPF" status={result.spf.status} value={result.spf.value} />
        <VerifyRow label="DKIM" status={result.dkim.status} value={result.dkim.value} />
        <VerifyRow label="DMARC" status={result.dmarc.status} value={result.dmarc.value} />
      </div>
      <div
        style={{
          marginTop: 16,
          padding: 12,
          background: allOk ? 'var(--spark-soft)' : 'var(--flame-soft)',
          border: `1px solid ${allOk ? 'var(--spark-line)' : 'var(--flame-line)'}`,
          borderRadius: 'var(--r-2)',
          fontSize: 13,
          color: allOk ? 'oklch(0.40 0.14 148)' : 'var(--flame-2)',
        }}
      >
        {allOk ? (
          <>
            <strong>Looks good.</strong> Your domain is authenticated. You can now connect your ESP credentials in
            the Providers screen and start sending.
          </>
        ) : (
          <>
            <strong>DNS isn't ready yet.</strong> Records can take 5–60 minutes to propagate. Re-check after a
            few minutes — most providers honor TTLs strictly.
          </>
        )}
      </div>
      <div style={{ marginTop: 12 }}>
        <Button onClick={onRecheck}>Re-check DNS</Button>
      </div>
    </Card>
  );
}

function VerifyRow({
  label,
  status,
  value,
}: {
  label: string;
  status: 'verified' | 'missing' | 'invalid';
  value: string | null;
}) {
  const tone = status === 'verified' ? 'live' : status === 'invalid' ? 'err' : 'flame';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: 10,
        border: '1px solid var(--line-soft)',
        borderRadius: 'var(--r-2)',
        background: 'var(--paper)',
      }}
    >
      <Chip variant={tone}>{status}</Chip>
      <div style={{ fontSize: 13, fontWeight: 550, width: 60 }}>{label}</div>
      <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', flex: 1, wordBreak: 'break-all' }}>
        {value ?? '(no record found)'}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      className="mf-card"
      style={{
        padding: 20,
        marginBottom: 16,
        background: 'var(--paper)',
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          letterSpacing: 0.06,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}
